import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRequest } from 'ahooks';
import {
  apiV2DistributionCabinetRoomCabinetListPost,
  apiV2EnergyResilienceConfigGasDropdownPost,
  apiV2EnergyResilienceConfigGasOutletGetGasSourcePost,
  apiV2EnergyResilienceConfigLineIdNameDropdownPost,
  apiV2EnergyResilienceOverviewDistributionPost,
  apiV2EnergyResilienceOverviewListPost,
  apiV2EnergyResilienceOverviewPlantPost,
} from '@maxtropy/device-customer-apis-v2';
import DistributionOverview from './DistributionOverview';
import PlantOverview from './PlantOverview';
import { EnergyMedium, compressorTab, electricTabs, energyOptions, gasTabs } from './constants';
import styles from './index.module.scss';

const toSelectOptions = (list?: any[]) => {
  return (list ?? [])
    .map(item => {
      if (typeof item === 'string') return { label: item, value: item };
      const label = item?.label ?? item?.name ?? item?.deviceName ?? item?.code;
      const value = item?.value ?? item?.name ?? item?.deviceName ?? item?.code;
      return label && value ? { label, value } : undefined;
    })
    .filter(Boolean);
};

const flattenRoomOptions = (list?: any[]): any[] => {
  return (list ?? []).flatMap(item => {
    const current = item?.type === 1 ? [] : [{ label: item?.name, value: item?.name }];
    return [...current, ...flattenRoomOptions(item?.children)];
  });
};

const symbolMap: Record<string, number> = {
  '>': 1,
  '>=': 2,
  '<': 3,
  '<=': 4,
};

const EnergyResilienceModal = () => {
  const [energyKey, setEnergyKey] = useState<EnergyMedium>(EnergyMedium.ELECTRIC);
  const [activeTab, setActiveTab] = useState('station');
  const [activeRange, setActiveRange] = useState('');
  const [name, setName] = useState<string>();
  const [rateOperator, setRateOperator] = useState('>');
  const [rateValue, setRateValue] = useState<number | null>(null);
  const [startPoint, setStartPoint] = useState<string>();
  const [endPoint, setEndPoint] = useState<string>();
  const [gasSource, setGasSource] = useState<string>();

  const currentEnergy = useMemo(
    () => energyOptions.find(item => item.key === energyKey) ?? energyOptions[0],
    [energyKey]
  );
  const isElectric = energyKey === EnergyMedium.ELECTRIC;

  const tabs = useMemo(() => {
    if (isElectric) return electricTabs;
    return energyKey === EnergyMedium.AIR ? [...gasTabs, compressorTab] : gasTabs;
  }, [energyKey, isElectric]);

  const resetFilters = useCallback(() => {
    setActiveRange('');
    setName(undefined);
    setRateValue(null);
    setStartPoint(undefined);
    setEndPoint(undefined);
    setGasSource(undefined);
  }, []);

  useEffect(() => {
    setActiveTab(isElectric ? 'station' : 'plant');
    resetFilters();
  }, [energyKey, isElectric, resetFilters]);

  useEffect(() => {
    resetFilters();
  }, [activeTab, resetFilters]);

  const baseParams = useMemo(
    () => ({
      mediumType: currentEnergy.mediumType,
      dimension: activeTab as any,
    }),
    [activeTab, currentEnergy.mediumType]
  );

  const { data: plantData, loading: plantLoading } = useRequest(
    async () => {
      return await apiV2EnergyResilienceOverviewPlantPost({ mediumType: currentEnergy.mediumType });
    },
    {
      ready: activeTab === 'plant',
      refreshDeps: [energyKey, activeTab],
    }
  );

  const { data: distributionData, loading: distributionLoading } = useRequest(
    async () => {
      return await apiV2EnergyResilienceOverviewDistributionPost(baseParams);
    },
    {
      ready: activeTab !== 'plant',
      refreshDeps: [baseParams],
    }
  );

  const { data: listData, loading: listLoading } = useRequest(
    async () => {
      const listParams = {
        ...baseParams,
        page: 1,
        size: 200,
        date: new Date().toISOString().slice(0, 10),
        loadRateRange: activeRange || undefined,
        loadRate: rateValue ?? undefined,
        symbol: rateValue == null ? undefined : symbolMap[rateOperator],
        stationName: activeTab === 'station' ? name : undefined,
        codeOrName: activeTab === 'line' ? name : undefined,
        circuitStart: activeTab === 'line' ? startPoint : undefined,
        circuitEnd: activeTab === 'line' ? endPoint : undefined,
        outletName: activeTab === 'outlet' ? name : undefined,
        gasSource: ['outlet', 'area'].includes(activeTab) ? gasSource : undefined,
        areaName: activeTab === 'area' ? name : undefined,
        compressorName: activeTab === 'compressor' ? name : undefined,
      };

      return await apiV2EnergyResilienceOverviewListPost(listParams);
    },
    {
      ready: activeTab !== 'plant',
      refreshDeps: [activeRange, activeTab, baseParams, endPoint, gasSource, name, rateOperator, rateValue, startPoint],
    }
  );

  const { data: stationNameOptions = [] } = useRequest(
    async () => {
      const res = await apiV2DistributionCabinetRoomCabinetListPost({});
      return flattenRoomOptions(res?.list).filter(item => item.label && item.value);
    },
    {
      ready: activeTab === 'station',
      refreshDeps: [activeTab],
    }
  );

  const { data: lineNameOptions = [] } = useRequest(
    async () => {
      const res = await apiV2EnergyResilienceConfigLineIdNameDropdownPost();
      return (res?.list ?? []).map(item => ({
        label: [item.name, item.code].filter(Boolean).join('/'),
        value: item.name || item.code,
      }));
    },
    {
      ready: activeTab === 'line',
      refreshDeps: [activeTab],
    }
  );

  const { data: outletNameOptions = [] } = useRequest(
    async () => {
      const res = await apiV2EnergyResilienceConfigGasDropdownPost({
        mediumType: currentEnergy.mediumType,
        dimension: 'outlet',
      });
      return toSelectOptions(res?.list);
    },
    {
      ready: activeTab === 'outlet',
      refreshDeps: [activeTab, currentEnergy.mediumType],
    }
  );

  const { data: areaNameOptions = [] } = useRequest(
    async () => {
      const res = await apiV2EnergyResilienceConfigGasDropdownPost({
        mediumType: currentEnergy.mediumType,
        dimension: 'area',
      });
      return toSelectOptions(res?.list);
    },
    {
      ready: activeTab === 'area',
      refreshDeps: [activeTab, currentEnergy.mediumType],
    }
  );

  const { data: compressorNameOptions = [] } = useRequest(
    async () => {
      const res = await apiV2EnergyResilienceConfigGasDropdownPost({
        mediumType: currentEnergy.mediumType,
        dimension: 'compressor',
      });
      return toSelectOptions(res?.list);
    },
    {
      ready: activeTab === 'compressor',
      refreshDeps: [activeTab, currentEnergy.mediumType],
    }
  );

  const { data: gasSourceOptions = [] } = useRequest(
    async () => {
      const res = await apiV2EnergyResilienceConfigGasOutletGetGasSourcePost({
        mediumType: currentEnergy.mediumType,
        dimension: activeTab,
      } as any);
      return (res?.list ?? []).map(item => ({
        label: item.zoneName,
        value: item.zoneName,
      }));
    },
    {
      ready: ['outlet', 'area'].includes(activeTab),
      refreshDeps: [activeTab, currentEnergy.mediumType],
    }
  );

  const nameOptions = useMemo(() => {
    if (activeTab === 'station') return stationNameOptions;
    if (activeTab === 'line') return lineNameOptions;
    if (activeTab === 'outlet') return outletNameOptions;
    if (activeTab === 'area') return areaNameOptions;
    if (activeTab === 'compressor') return compressorNameOptions;
    return [];
  }, [activeTab, areaNameOptions, compressorNameOptions, lineNameOptions, outletNameOptions, stationNameOptions]);

  const filters = useMemo(
    () => ({ activeRange, name, startPoint, endPoint, gasSource, rateValue, rateOperator }),
    [activeRange, endPoint, gasSource, name, rateOperator, rateValue, startPoint]
  );

  const handleRangeClick = useCallback((range: string) => {
    setActiveRange(prev => (prev === range ? '' : range));
  }, []);

  const handleRateValueChange = useCallback((value: any) => {
    setRateValue(value == null ? null : Number(value));
  }, []);

  const handleDetail = useCallback(
    (record?: any) => {
      const query = new URLSearchParams({
        mediumType: String(currentEnergy.mediumType ?? 0),
        dimension: activeTab,
        date: new Date().toISOString().slice(0, 10),
      });

      if (record?.configId) {
        query.set('configId', String(record.configId));
      }

      window.open(`/energy/resilience/analysis?${query.toString()}`, '_blank');
    },
    [activeTab, currentEnergy.mediumType]
  );

  const filterSetters = useMemo(
    () => ({
      setName,
      setStartPoint,
      setEndPoint,
      setGasSource,
      setRateOperator,
      setRateValue: handleRateValueChange,
    }),
    [handleRateValueChange]
  );

  return (
    <div className={styles.container}>
      <div className={styles.side}>
        {energyOptions.map(item => (
          <div
            key={item.key}
            className={`${styles.energyItem} ${energyKey === item.key ? styles.energyActive : ''}`}
            onClick={() => setEnergyKey(item.key)}
          >
            {item.label}
          </div>
        ))}
      </div>
      <div className={styles.content}>
        <div className={styles.tabs}>
          {tabs.map(item => (
            <div
              key={item.key}
              className={`${styles.tab} ${activeTab === item.key ? styles.tabActive : ''}`}
              onClick={() => setActiveTab(item.key)}
            >
              {item.label}
            </div>
          ))}
        </div>

        {activeTab === 'plant' ? (
          <PlantOverview data={plantData} loading={plantLoading} onDetail={handleDetail} />
        ) : (
          <DistributionOverview
            activeTab={activeTab}
            activeRange={activeRange}
            distributionData={distributionData}
            loading={distributionLoading || listLoading}
            sourceList={listData?.list ?? []}
            filteredList={listData?.list ?? []}
            nameOptions={nameOptions}
            gasSourceOptions={gasSourceOptions}
            filters={filters}
            setters={filterSetters}
            onRangeClick={handleRangeClick}
            onRangeReset={() => setActiveRange('')}
            onDetail={handleDetail}
          />
        )}
      </div>
    </div>
  );
};

export default EnergyResilienceModal;
